/**
 * Rented from a vendor — `docs/visit-page.md` §4.6, §10.2 (Board 5 A's cards,
 * Board 6 A *To book*, B the sheet) and §27.4 (Board 18 I, while the crew app
 * has no rental stops).
 *
 * *Rental* alone still means renting something of ours out
 * (`rentals-and-site-assets.md`); this is what the company rents **in**.
 */
import { z } from 'zod';
import { RENTAL_HOWS } from './values.js';
// ---------------------------------------------------------------------------
// The sheet (Board 6 B) — GET /visits/:id/rentals/options, POST …/rentals/preview
// ---------------------------------------------------------------------------
/** `?rentalId=` opens **Book it** / **Change** on that rental; `?vendorId=` re-reads with a vendor picked. */
export const rentalOptionsQuerySchema = z.object({
    rentalId: z.string().uuid().optional(),
    vendorId: z.string().uuid().optional(),
});
// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------
const money = z.string().trim().regex(/^\d+(\.\d{1,2})?$/, 'A rate, like 95.00.');
const moment = z.string().datetime({ offset: true });
/**
 * The Rent sheet's fields, with no defaults — so a PATCH or a preview built
 * from them leaves an absent key absent (zod 4 applies a default inside
 * `.partial()`; `tests/zod-schemas.test.ts`).
 */
const rentalFields = {
    /** **What** (required) — *Towable concrete mixer*. */
    description: z.string().trim().min(1, 'Say what.').max(200),
    /** **Type** (optional) — *Concrete mixer*. */
    typeId: z.string().uuid().nullish(),
    /** **Vendor** (required). */
    vendorId: z.string().uuid({ message: 'Pick the vendor.' }),
    /** **Days** — chips per day, several allowed. */
    dayIndexes: z.array(z.number().int().min(1).max(366)).min(1, 'Pick a day.'),
    /** *Crew picks up · Vendor delivers to the site*. */
    how: z.enum(RENTAL_HOWS),
    /** **Picks up** + **At** (*at the site about 7:30*). */
    pickupUserId: z.string().uuid().nullish(),
    pickupAt: moment.nullish(),
    /** **Takes it back** + **By**. */
    returnUserId: z.string().uuid().nullish(),
    returnBy: moment.nullish(),
    /** **Rate** (internal) — a day. */
    rate: money,
    /** **Confirmation** (optional) — *KR-4471*. */
    confirmation: z.string().trim().max(120),
    /** **Note for the crew** (optional). */
    crewNote: z.string().trim().max(2000),
};
/**
 * `POST /visits/:id/rentals` — **Book** (Board 6 B). *What*, *Vendor* and a
 * day are required (§22 test 18).
 */
export const rentalSchema = z.object({
    ...rentalFields,
    /**
     * Internal: left out by a person without *Costs & margins*, who is never
     * shown it (§2.3) — the server keeps the planned rate, else none.
     */
    rate: rentalFields.rate.optional(),
    confirmation: rentalFields.confirmation.default(''),
    crewNote: rentalFields.crewNote.default(''),
});
/**
 * `PATCH /visits/:id/rentals/:rid` — **Change**, and **Book it** on a *To
 * book* rental (it becomes *Booked* once it has a vendor and its days). Only
 * the keys sent change.
 */
export const rentalPatchSchema = z.object(rentalFields).partial().extend({
    version: z.number().int().min(1),
});
/** `POST /visits/:id/rentals/preview` — the sheet as typed so far, any field missing. */
export const rentalPreviewSchema = z.object(rentalFields).partial();
/** `POST /visits/:id/rentals/:rid/cancel {reason}` — **Cancel booking**. */
export const cancelRentalSchema = z.object({
    version: z.number().int().min(1),
    reason: z.string().trim().min(1, 'Say why.').max(2000),
});
/**
 * `POST /visits/:id/rentals/:rid/picked-up` and `…/returned` — **Mark picked
 * up** / **Mark returned** (Board 18 I, through `CrewPort`'s stand-in): sets
 * `picked_up_*` / `returned_*` with the office person as `*_by`, now.
 */
export const markRentalStopSchema = z.object({
    version: z.number().int().min(1),
});
//# sourceMappingURL=rentals.js.map